import { Gift, Handshake, BadgeCheck, ArrowUpRight, Plus } from 'lucide-react';
import { AxudaLogo } from './AxudaLogo';

interface Partner {
  name: string;
  tagline: string;
  description: string;
  /** Highlighted (featured) partner gets a subtle spotlight treatment. */
  featured?: boolean;
  logo: React.ReactNode;
  /** Tailwind gradient classes used for the accent ring / badge glow. */
  accent: string;
}

const PARTNERS: Partner[] = [
  {
    name: 'AXUDA',
    tagline: 'Partner sprzedażowy i pozyskiwanie klientów',
    description:
      'Partner sprzedażowy AI Agent HUB — pośredniczy w sprzedaży i pozyskuje nowych klientów, budując zasięg platformy i wspierając jej rozwój oraz skalowanie.',
    featured: true,
    logo: <AxudaLogo size={56} />,
    accent: 'from-emerald-400 via-teal-500 to-cyan-600',
  },
  {
    name: 'Gapli',
    tagline: 'Platforma marketingu partnerskiego',
    description:
      'Zintegrowany partner kampanii reklamowych — gotowe kreacje i szablony Meta Ads uruchamiane bezpośrednio z poziomu HUB-a jednym kliknięciem.',
    logo: (
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
        <Gift className="w-7 h-7 text-white" />
      </div>
    ),
    accent: 'from-purple-500 via-fuchsia-500 to-pink-500',
  },
];

export function StrategicPartners() {
  return (
    <section className="w-full bg-gradient-to-b from-white to-slate-50 border-t border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-semibold uppercase tracking-wider mb-4">
            <Handshake className="w-3.5 h-3.5" />
            Współpraca
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Partnerzy Strategiczni
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-500 leading-relaxed">
            Rozwijamy AI Agent HUB razem z zaufanymi partnerami technologicznymi.
            Wspólnie dostarczamy kompleksowe rozwiązania automatyzacji, marketingu
            i sztucznej inteligencji dla nowoczesnego biznesu.
          </p>
        </div>

        {/* Partner grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {PARTNERS.map((p) => (
            <div
              key={p.name}
              className={`group relative flex flex-col rounded-2xl border bg-white p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                p.featured
                  ? 'border-teal-200 shadow-lg shadow-teal-500/5 ring-1 ring-teal-100'
                  : 'border-slate-200 shadow-sm hover:border-slate-300'
              }`}
            >
              {/* Accent glow on top edge */}
              <div
                className={`absolute inset-x-6 top-0 h-px bg-gradient-to-r ${p.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />

              {p.featured && (
                <span className="absolute top-5 right-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[11px] font-bold uppercase tracking-wide">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Główny partner
                </span>
              )}

              <div className="flex items-center gap-4 mb-5">
                {p.logo}
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{p.name}</h3>
                  <p className="text-sm text-slate-500">{p.tagline}</p>
                </div>
              </div>

              <div className="inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200 text-slate-600 text-xs font-semibold mb-4">
                <BadgeCheck className="w-3.5 h-3.5 text-teal-500" />
                Partner Strategiczny
              </div>

              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {p.description}
              </p>
            </div>
          ))}

          {/* Become a partner CTA */}
          <a
            href="mailto:partner@aiagenthub.pl?subject=Wsp%C3%B3%C5%82praca%20partnerska%20-%20AI%20Agent%20HUB"
            className="group relative flex flex-col items-center justify-center text-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-6 sm:p-7 transition-all duration-300 hover:border-blue-300 hover:bg-blue-50/40"
          >
            <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm mb-4 group-hover:border-blue-200 group-hover:scale-105 transition-all">
              <Plus className="w-7 h-7 text-slate-400 group-hover:text-blue-500 transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Zostań partnerem</h3>
            <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
              Dołącz do grona partnerów strategicznych AI Agent HUB i rozwijaj
              swój biznes razem z nami.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
              Napisz do nas
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </a>
        </div>

        {/* Trust footnote */}
        <p className="text-center text-xs text-slate-400 mt-10">
          Zaufane partnerstwa technologiczne · Wspólne wdrożenia · Długoterminowa współpraca
        </p>
      </div>
    </section>
  );
}

export default StrategicPartners;
